import React from 'react';
import { MapPin, DollarSign, Briefcase, Calendar, ChevronRight, Phone, Mail, PhoneOff } from 'lucide-react';
import { TooltipContext } from './TooltipContext';
import TimedPill from './TimedPill';
import PillList from './PillList';
import InfoRow from './InfoRow';
import ListingSourceBadge from './ListingSourceBadge';
import TrustBadgeRow, { listingLevelBadges } from '../shared/TrustBadgeRow';
import LicenseMatchBadges from '../shared/LicenseMatchBadges';
import { htmlToPlainText } from '../../utils/htmlToPlainText';
import MarketBandTooltip from './MarketBandTooltip';
import './ExternalListingFront.css';
import { formatPostedPeriodRate } from '../../utils/postedRate';

const dash = '–';

/**
 * Format relative time from a date (e.g., "2 days ago", "3 weeks ago")
 * Handles future dates gracefully by showing "Recently" instead of negative days
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Handle future dates (bad data) - show as "Recently" instead of negative days
    if (diffDays < 0) return 'Recently';

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60) return '1 month ago';
    return `${Math.floor(diffDays / 30)} months ago`;
  } catch (e) {
    return null;
  }
};

/**
 * Format a single compensation option (hourly or annual)
 */
const formatSingleCompensation = (hourlyAmount, maxHourlyAmount, annualAmount, maxAnnualAmount) => {
  if (hourlyAmount > 0) {
    if (maxHourlyAmount > 0 && maxHourlyAmount !== hourlyAmount) {
      return `$${Math.round(hourlyAmount)}-${Math.round(maxHourlyAmount)}/hr`;
    }
    return `$${Math.round(hourlyAmount)}/hr`;
  }

  if (annualAmount > 0) {
    const minK = Math.round(annualAmount / 1000);
    if (maxAnnualAmount > 0 && maxAnnualAmount !== annualAmount) {
      const maxK = Math.round(maxAnnualAmount / 1000);
      return `$${minK}-${maxK}K/yr`;
    }
    return `$${minK}K/yr`;
  }

  return null;
};

/**
 * Format compensation for display
 * Returns object with { display: string, hasCompensation: boolean, hasDualCompensation: boolean, w2Display: string, comp1099Display: string }
 * 
 * Shows range when both min and max are available (e.g., "280-600K/yr")
 * Supports dual 1099/W2 compensation options
 * Also handles GasWork-style fields (minIncome, maxIncome, isHourlyRate)
 */
const formatCompensation = (listing) => {
  const compensation = listing.compensation || {};

  // Backend sets hasCompensation: false if the values are invalid (e.g., bonuses)
  if (compensation.hasCompensation === false) {
    return { display: 'Compensation Not Listed', hasCompensation: false, hasDualCompensation: false };
  }

  // Period-quoted locum: show the verbatim posted rate in the unit the poster
  // used, never the ×52 annual or the ÷hours hourly the backend also stores for
  // matching. This handled 'weekly' only, so a per-day or per-shift posting
  // fell through to the derived hourly and was shown as if it were posted --
  // the "$2,200/day" role that read as "$275/hr". Falls back to a bare
  // weeklyAmount (gaswork/matcher extractor path).
  const postedPeriodRate = formatPostedPeriodRate(compensation);
  if (postedPeriodRate) {
    return {
      display: postedPeriodRate,
      hasCompensation: true,
      hasDualCompensation: false,
    };
  }

  // Check for dual compensation (both 1099 and W2 with different rates)
  const has1099 = compensation.compensation1099 && (compensation.compensation1099.annualAmount || compensation.compensation1099.hourlyAmount);
  const hasW2 = compensation.w2Compensation && (compensation.w2Compensation.annualAmount || compensation.w2Compensation.hourlyAmount);

  if (has1099 && hasW2) {
    const comp1099 = compensation.compensation1099;
    const w2Comp = compensation.w2Compensation;

    const comp1099Display = formatSingleCompensation(
      comp1099.hourlyAmount || 0,
      comp1099.maxHourlyAmount || 0,
      comp1099.annualAmount || 0,
      comp1099.maxAnnualAmount || 0
    );

    const w2Display = formatSingleCompensation(
      w2Comp.hourlyAmount || 0,
      w2Comp.maxHourlyAmount || 0,
      w2Comp.annualAmount || 0,
      w2Comp.maxAnnualAmount || 0
    );

    if (comp1099Display && w2Display) {
      return {
        display: `1099: ${comp1099Display}`,
        hasCompensation: true,
        hasDualCompensation: true,
        w2Display: `W2: ${w2Display}`,
        comp1099Display: `1099: ${comp1099Display}`,
      };
    }
  }

  // Use the sanitized values from the compensation object (single compensation type)
  let hourlyAmount = compensation.hourlyAmount || 0;
  let maxHourlyAmount = compensation.maxHourlyAmount || 0;
  let annualAmount = compensation.annualAmount || 0;
  let maxAnnualAmount = compensation.maxAnnualAmount || 0;

  // GasWork-style fields: minIncome, maxIncome, isHourlyRate, annualizedMinIncome, annualizedMaxIncome
  // These are stored directly on the listing object, not in compensation
  if (!hourlyAmount && !annualAmount) {
    const minIncome = listing.minIncome || 0;
    const maxIncome = listing.maxIncome || 0;
    const isHourlyRate = listing.isHourlyRate === true;
    const annualizedMin = listing.annualizedMinIncome || 0;
    const annualizedMax = listing.annualizedMaxIncome || 0;

    if (isHourlyRate && minIncome > 0) {
      hourlyAmount = minIncome;
      maxHourlyAmount = maxIncome > minIncome ? maxIncome : 0;
    } else if (annualizedMin > 0) {
      annualAmount = annualizedMin;
      maxAnnualAmount = annualizedMax > annualizedMin ? annualizedMax : 0;
    } else if (minIncome > 0) {
      // Assume annual if not hourly
      annualAmount = minIncome;
      maxAnnualAmount = maxIncome > minIncome ? maxIncome : 0;
    }
  }

  const display = formatSingleCompensation(hourlyAmount, maxHourlyAmount, annualAmount, maxAnnualAmount);

  if (display) {
    return { display, hasCompensation: true, hasDualCompensation: false };
  }

  return { display: 'Compensation Not Listed', hasCompensation: false, hasDualCompensation: false };
};

/**
 * Truncate text to a maximum length with ellipsis
 */
const truncateText = (text, maxLength = 120) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * ExternalListingFront - Optimized card front for external (GasWork) listings
 * Shows the data that IS available and hides empty fields
 */
const ExternalListingFront = ({ listing = {}, sourceType = 'external', alignmentData = null, viewerType = 'provider', onOpenDetails = null }) => {
  const [openId, setOpenId] = React.useState(null);

  // D1.11.2: a card face carries organization- and requisition-level awards
  // only; recruiter-level ones render on conversation screens.
  const orgTrustBadges = listingLevelBadges(listing.trustBadges);

  // Location - always available
  const city = listing.city && listing.city !== 'Unknown' ? listing.city : null;
  const state = listing.state || '';
  const location = city ? `${city}, ${state}` : state || 'Location TBD';

  // Provider type (CRNA or Anesthesiologist)
  const providerType = listing.section || listing.providerType || 'CRNA';

  // Contact info availability
  const hasPhone = listing._hasPhone || !!listing.contactPhone || false;
  const hasEmail = listing._hasEmail || !!listing.contactEmail || false;
  const hasName = listing._hasName || !!listing.contactName || false;
  const hasChannelContact = hasPhone || hasEmail || hasName;
  const hasGenericContact =
    !hasChannelContact &&
    !!(listing._hasContactInfo || listing.hasContactInfo);
  const hasAnyContact = hasChannelContact || hasGenericContact;

  // Compensation - supports dual 1099/W2 options
  const { display: compensationDisplay, hasCompensation, hasDualCompensation, w2Display } = formatCompensation(listing);

  // Any listing whose rate is not shown (comp-blind: no rate was ever posted, or
  // a blind listing hiding a real rate) gets the unified "Competitive Rate" badge
  // in place of "Compensation Not Listed". When the backend resolved a market
  // band it drives a tap-to-reveal honest range; with no band the badge shows
  // "Competitive Rate" with no tooltip.
  //
  // isBlind/isCompBlind are checked BEFORE hasCompensation, not gated behind it.
  // A blind listing has a real rate and formatCompensation finds it, so
  // `!hasCompensation` was false exactly when the hiding mattered and the card
  // printed the number it was meant to withhold. Only the marketBand fallback
  // still defers to hasCompensation: a band is a nice-to-have on a listing with
  // no rate of its own, never a reason to suppress one that publishes.
  // Matches ListingFront.jsx, which has always read the flags directly.
  const rateWithheld = !!(listing.compensation?.isCompBlind || listing.compensation?.isBlind);
  const rateNotShown = rateWithheld || (!hasCompensation && !!listing.marketBand);
  const marketBandTip = listing.marketBand ? (
    <MarketBandTooltip
      providerType={listing.providerType || listing.section}
      state={state}
      canonicalCategory={listing.assignment?.canonicalCategory}
      band={listing.marketBand}
    />
  ) : null;

  // Contract type - try to extract from compensation or w2Status.
  // For dual compensation, contractType will be "Both".
  // When no source signals the type, leave it empty so the guard below hides
  // the Type chip rather than asserting a concrete value (e.g. "1099") for a
  // genuinely-unknown listing.
  const contractType = listing.compensation?.contractType ||
    listing.w2Status ||
    listing.externalMeta?.w2Status ||
    '';

  // Duration/Assignment type - always available per audit
  const duration = listing.duration ||
    listing.assignment?.types?.[0] ||
    listing.assignment?.duration ||
    'Position Type TBD';

  // Posted date - always available per audit
  const postedDate = listing.datePosted || listing.createdAt;
  const relativeDate = formatRelativeTime(postedDate);

  // Brief description - always available per audit
  const briefDescription = listing.briefDescription ||
    listing.title ||
    listing.summary ||
    '';
  // briefDescription/title/summary all arrive from the source feed, so any of
  // them can carry HTML. Converting BEFORE the 140-char cut matters twice over:
  // slicing the raw string would spend the preview budget on tag characters,
  // and it could leave a half-written tag sitting on the card. The preview is a
  // single line-clamped run, so the block breaks the converter emits collapse
  // back to spaces here rather than being rendered.
  const descriptionPreview = htmlToPlainText(briefDescription).replace(/\n+/g, ' ');
  const truncatedDescription = truncateText(descriptionPreview, 140);

  // Priority level for visual indicator
  const priorityLevel = listing.priorityLevel || 'General';
  const isHighPriority = priorityLevel === 'HighPriority' || priorityLevel === 'Priority';

  // Define reusable field components for ordering
  const LocationRow = (
    <InfoRow Icon={MapPin} alignmentStatus={alignmentData?.location}>
      <PillList
        items={[location]}
        getTooltip={() => 'Job location'}
        idPrefix="location"
        alignmentStatus={alignmentData?.location}
      />
    </InfoRow>
  );

  const PayRow = (
    <InfoRow Icon={DollarSign} alignmentStatus={alignmentData?.compensation}>
      <div className="pay-contract-details">
        {/* rateNotShown is tested FIRST. A blind listing can carry dual 1099/W2
            comp, and this branch used to run ahead of the withheld check, so
            those listings printed BOTH hidden rates. Withholding outranks every
            way of displaying a rate. */}
        {rateNotShown ? (
          // Rate not shown (comp-blind or blind): unified "Competitive Rate"
          // badge. A resolved market band reveals an honest range on tap; with
          // no band there is no tooltip. Never the listing's own offer.
          <TimedPill
            id="pay-rate"
            label="Competitive Rate"
            tooltipText={marketBandTip || undefined}
            tooltipClassName="market-band-tip"
            className="market-band-pill"
            alignmentStatus={alignmentData?.compensation}
          />
        ) : hasDualCompensation ? (
          // Dual compensation: show both 1099 and W2 rates
          <>
            <TimedPill
              id="pay-rate-1099"
              label={compensationDisplay}
              tooltipText="1099 contractor compensation"
              className="compensation-available compensation-1099"
              alignmentStatus={alignmentData?.compensation}
            />
            <TimedPill
              id="pay-rate-w2"
              label={w2Display}
              tooltipText="W2 employee compensation (includes benefits)"
              className="compensation-available compensation-w2"
              alignmentStatus={alignmentData?.compensation}
            />
          </>
        ) : (
          // Single compensation type
          <>
            <TimedPill
              id="pay-rate"
              label={compensationDisplay}
              tooltipText={hasCompensation ? 'Compensation for this position' : 'Compensation not specified in listing'}
              className={hasCompensation ? 'compensation-available' : 'compensation-missing'}
              alignmentStatus={alignmentData?.compensation}
            />
            {/* Show contract type inline for provider view */}
            {viewerType === 'provider' && hasCompensation && contractType && contractType !== dash && contractType !== 'Both' && (
              <PillList
                items={[contractType]}
                getTooltip={() => 'W2 = employed; 1099 = contract'}
                idPrefix="contract"
                alignmentStatus={alignmentData?.contractType}
              />
            )}
          </>
        )}
      </div>
    </InfoRow>
  );

  const PostedDateRow = relativeDate ? (
    <InfoRow Icon={Calendar}>
      <TimedPill
        id="posted-date"
        label={`Posted ${relativeDate}`}
        tooltipText={postedDate ? `Posted on ${new Date(postedDate).toLocaleDateString()}` : 'Posting date'}
      />
    </InfoRow>
  ) : null;

  const DurationRow = (
    <InfoRow Icon={Briefcase} alignmentStatus={alignmentData?.workType}>
      <PillList
        items={[duration]}
        getTooltip={type => `Position type: ${type}`}
        idPrefix="assignment"
        alignmentStatus={alignmentData?.workType}
      />
    </InfoRow>
  );

  // Render fields in role-based order
  // Group users: Posted Date → Location → Pay → Duration
  // Provider users: Pay → Location → Posted Date → Duration
  const renderOrderedFields = () => {
    if (viewerType === 'group') {
      return (
        <>
          {PostedDateRow}
          {LocationRow}
          {PayRow}
          {DurationRow}
        </>
      );
    }
    // Provider (default) view
    return (
      <>
        {PayRow}
        {LocationRow}
        {PostedDateRow}
        {DurationRow}
      </>
    );
  };

  return (
    <TooltipContext.Provider value={{ openId, setOpenId }}>
      <div className="card-face-content listing-gradient-bg external-listing-front" role="region" aria-label="External Listing Front">
        {/* Header with provider type - structure matches ProfileFront.jsx */}
        <div className="card-header">
          <h2 className="card-title external-card-title">
            {providerType} Position
            {isHighPriority && (
              <span className="priority-indicator">⭐ Priority</span>
            )}
          </h2>
          <div className="badges-container">
            <LicenseMatchBadges relevance={listing.licenseRelevance} />
            <ListingSourceBadge
              sourceType={sourceType}
              verified={!!(listing.posterVerified || listing.posterVerifiedAt)}
              orgVerified={orgTrustBadges.some(
                (b) => b.key === 'verified_employer',
              )}
            />
            {orgTrustBadges.length > 0 && (
              <TrustBadgeRow badges={orgTrustBadges} />
            )}
          </div>
        </div>

        {renderOrderedFields()}

        {/* Contact info availability indicators - show each field individually */}
        <InfoRow Icon={hasAnyContact ? Phone : PhoneOff}>
          <div className="contact-availability-row">
            {hasName && (
              <TimedPill
                id="contact-name"
                label="Name"
                tooltipText="Contact person's name is available"
                className="contact-available contact-name"
              />
            )}
            {hasEmail && (
              <TimedPill
                id="contact-email"
                label="Email"
                tooltipText="Email contact is available"
                className="contact-available contact-email"
              />
            )}
            {hasPhone && (
              <TimedPill
                id="contact-phone"
                label="Phone"
                tooltipText="Phone contact is available"
                className="contact-available contact-phone"
              />
            )}
            {hasGenericContact && (
              <TimedPill
                id="contact-available"
                label="Contact Available"
                tooltipText="A direct contact is available for this listing"
                className="contact-available"
              />
            )}
            {!hasAnyContact && (
              <TimedPill
                id="contact-none"
                label="No Direct Contact"
                tooltipText="Contact info not available - may require application through website"
                className="contact-missing"
              />
            )}
          </div>
        </InfoRow>

        {/* Description preview - the main value of external listings */}
        {truncatedDescription && (
          <button
            type="button"
            className="external-description-section external-description-section--button"
            onClick={(event) => {
              if (!onOpenDetails) return;
              event.stopPropagation();
              onOpenDetails();
            }}
            aria-label="Open the complete listing description and links"
          >
            <p className="external-description-text">
              {truncatedDescription}
            </p>
            <div className="external-description-cta">
              <span>Tap to see full details</span>
              <ChevronRight size={14} />
            </div>
          </button>
        )}
      </div>
    </TooltipContext.Provider>
  );
};

export default ExternalListingFront;
